import { useCallback, useEffect, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { trpc } from '@/lib/trpc'

interface EditListModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  listId: string
  currentName: string
  currentDescription: string | null
}

export function EditListModal({
  open,
  onOpenChange,
  listId,
  currentName,
  currentDescription,
}: EditListModalProps) {
  const [name, setName] = useState(currentName)
  const [description, setDescription] = useState(currentDescription ?? '')
  const utils = trpc.useUtils()

  // Sync state when modal opens with fresh data
  useEffect(() => {
    if (open) {
      setName(currentName)
      setDescription(currentDescription ?? '')
    }
  }, [open, currentName, currentDescription])

  const updateMutation = trpc.lists.update.useMutation({
    onSuccess: () => {
      utils.lists.getUserListData.invalidate()
      utils.lists.getListDetail.invalidate({ listId })
      onOpenChange(false)
      document.dispatchEvent(new Event('tour:lists:edit-saved'))
    },
    onError: (err) => {
      toast.error(err.message || 'Failed to update list')
    },
  })

  const nameRef = useRef(name)
  const descriptionRef = useRef(description)
  nameRef.current = name
  descriptionRef.current = description

  const handleSave = useCallback(() => {
    updateMutation.mutate({
      listId,
      name: nameRef.current.trim(),
      description: descriptionRef.current.trim() || undefined,
    })
  }, [updateMutation, listId])

  useEffect(() => {
    if (!open) return

    const onSetDescription = (e: Event) => {
      const detail = (e as CustomEvent).detail
      if (detail?.description != null) {
        setDescription(detail.description as string)
      }
    }

    const onSave = () => {
      handleSave()
    }

    document.addEventListener('tour:lists:set-description', onSetDescription)
    document.addEventListener('tour:lists:save-edit', onSave)

    return () => {
      document.removeEventListener(
        'tour:lists:set-description',
        onSetDescription,
      )
      document.removeEventListener('tour:lists:save-edit', onSave)
    }
  }, [open, handleSave])

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Edit list info</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-4">
          <div>
            <label className="mb-1 block text-sm font-medium">Title</label>
            <div className="relative">
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                maxLength={40}
              />
              <span className="text-muted-foreground absolute right-2 top-1/2 -translate-y-1/2 text-xs">
                {name.length}/40
              </span>
            </div>
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium">
              Description
            </label>
            <div className="relative">
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                maxLength={150}
                rows={3}
                className="border-input bg-background ring-offset-background placeholder:text-muted-foreground focus-visible:ring-ring w-full rounded-md border px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
              />
              <span className="text-muted-foreground text-xs">
                {description.length}/150
              </span>
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={!name.trim() || updateMutation.isPending}
            >
              Save
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
